import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, MessageSquare, ChevronLeft, Send, CheckCircle2, Clock, UploadCloud, FileSignature, Search, ArrowUp, ArrowDown, ChevronsUpDown, Info } from "lucide-react";
import { toast } from "sonner";
import { typeLabel } from "@/lib/constants";
import { AttachControls, AttachmentList } from "@/components/wp/QueryPanel";
import { DictateButton } from "@/components/DictateButton";

const STATUS = { planning: "Planning", fieldwork: "In progress", awaiting_review: "Under review", review_points: "Under review", partner_review: "Under review", approved: "Approved — preparing final", awaiting_client_approval: "Awaiting your approval", finalised: "Finalised" };
// Order used for the client-facing overview tiles.
const STATUS_STAGES = ["Planning", "In progress", "Under review", "Approved — preparing final", "Awaiting your approval", "Finalised"];
const STAGE_TONE = {
    "Planning": "text-slate-700",
    "In progress": "text-brand",
    "Under review": "text-indigo-700",
    "Approved — preparing final": "text-sky-700",
    "Awaiting your approval": "text-amber-600",
    "Finalised": "text-emerald-700",
};
const STAGE_DESC = {
    "Planning": "We've accepted the job and are getting set up — collecting your information and planning the work. Nothing is needed from you unless we ask.",
    "In progress": "Our team is actively preparing your service charge accounts and working through the file.",
    "Under review": "The accounts have been prepared and are being checked and signed off internally by a manager and/or partner.",
    "Approved — preparing final": "Approved internally — we're preparing the final version of the accounts ready for issue.",
    "Awaiting your approval": "The draft accounts are ready for you to review and approve before we finalise them.",
    "Finalised": "The accounts are complete, signed and issued. This job is done.",
};

export function PortalLayout({ children }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    return (
        <div className="min-h-screen bg-[#f4f5f2]" data-testid="portal-layout">
            <header className="bg-brand-ink text-white">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
                    <img src="/qube-logo.svg" alt="Qube" className="w-8 h-8 rounded-sm" />
                    <div>
                        <div className="font-display font-bold text-sm leading-tight">Qube Accountants · Client portal</div>
                        <div className="text-[11px] text-white/60" data-testid="portal-agent-name">{user?.managing_agent || "Managing agent"}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-4 text-xs">
                        <span className="text-white/70">{user?.name}</span>
                        <button data-testid="portal-logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="flex items-center gap-1 text-white/70 hover:text-white"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
                    </div>
                </div>
            </header>
            <main className="max-w-5xl mx-auto px-6 py-8">{children}</main>
        </div>
    );
}

export default function PortalHome() {
    const [items, setItems] = useState(null);
    const [actions, setActions] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [q, setQ] = useState("");
    const [sort, setSort] = useState({ key: "client_name", dir: "asc" });
    const nav = useNavigate();
    useEffect(() => { api.get("/portal/engagements").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail))); }, []);
    useEffect(() => { api.get("/portal/next-actions").then((r) => setActions(r.data.actions)).catch(() => setActions([])); }, []);
    useEffect(() => { api.get("/portal/contacts").then((r) => setContacts(r.data || [])).catch(() => setContacts([])); }, []);
    const toneCls = { amber: "border-amber-300 bg-amber-50", rose: "border-rose-300 bg-rose-50" };

    const statusLabel = (e) => STATUS[e.status] || e.status;
    const progressPct = (e) => (e.progress.total ? e.progress.done / e.progress.total : 0);

    const summary = useMemo(() => {
        const counts = Object.fromEntries(STATUS_STAGES.map((s) => [s, 0]));
        let queries = 0;
        (items || []).forEach((e) => { const l = statusLabel(e); if (l in counts) counts[l] += 1; queries += e.awaiting_you || 0; });
        return { counts, queries, total: (items || []).length };
    }, [items]);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        let list = (items || []).filter((e) => {
            if (!needle) return true;
            const hay = [e.client_name, e.property_name, e.reference, e.address, e.current_period_end?.slice(0, 10), statusLabel(e), e.ma_contact, e.preparer_name].join(" ").toLowerCase();
            return hay.includes(needle);
        });
        const dir = sort.dir === "asc" ? 1 : -1;
        const val = (e) => {
            switch (sort.key) {
                case "property_name": return (e.property_name || "").toLowerCase();
                case "reference": return (e.reference || "").toLowerCase();
                case "address": return (e.address || "").toLowerCase();
                case "current_period_end": return e.current_period_end || "";
                case "status": return statusLabel(e).toLowerCase();
                case "progress": return progressPct(e);
                case "queries": return e.awaiting_you || 0;
                case "ma_contact": return (e.ma_contact || "").toLowerCase();
                case "preparer_name": return (e.preparer_name || "").toLowerCase();
                default: return (e.client_name || "").toLowerCase();
            }
        };
        return list.slice().sort((a, b) => { const va = val(a), vb = val(b); return va < vb ? -dir : va > vb ? dir : 0; });
    }, [items, q, sort]);

    const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
    const saveField = async (e, field, value) => {
        const v = (value || "").trim();
        if (v === (e[field] || "")) return;
        try { await api.patch(`/portal/engagements/${e.id}/client-fields`, { [field]: v }); setItems((prev) => prev.map((x) => x.id === e.id ? { ...x, [field]: v } : x)); toast.success("Saved"); }
        catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    };

    const Th = ({ label, k, className = "" }) => (
        <th className={`px-3 py-2 font-display text-[10px] uppercase tracking-widest text-muted-foreground select-none ${className}`}>
            <button data-testid={`portal-sort-${k}`} onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                {label}
                {sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ChevronsUpDown className="w-3 h-3 opacity-40" />}
            </button>
        </th>
    );

    return (
        <PortalLayout>
            {actions && actions.length > 0 && (
                <div className="mb-8" data-testid="portal-next-actions">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-display mb-2">Your next action{actions.length > 1 ? "s" : ""}</div>
                    <div className="grid gap-2">
                        {actions.map((a, i) => (
                            <Link key={i} to={a.link} data-testid={`portal-action-${a.kind}-${i}`} className={`flex items-center gap-3 border rounded-sm p-4 hover:shadow-sm transition-shadow ${toneCls[a.tone] || "border-border bg-white"}`}>
                                <Clock className="w-5 h-5 shrink-0 text-amber-600" />
                                <div className="flex-1 min-w-0"><div className="font-display font-semibold truncate">{a.title}</div><div className="text-xs text-muted-foreground">{a.detail}</div></div>
                                <ChevronLeft className="w-4 h-4 rotate-180 text-muted-foreground shrink-0" />
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-1">Your service charge accounts</div>
            <h1 className="font-display font-bold text-3xl tracking-tight mb-5">Overview</h1>

            {/* Overview summary tiles — client-facing stages */}
            {items !== null && items.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-8" data-testid="portal-overview">
                    <div className="bg-brand-ink text-white rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-widest text-white/60 font-display">Total jobs</div>
                        <div className="text-2xl font-display font-bold" data-testid="portal-overview-total">{summary.total}</div>
                    </div>
                    {STATUS_STAGES.map((s) => (
                        <button key={s} onClick={() => setQ(s)} title={STAGE_DESC[s]} data-testid={`portal-overview-${s.replace(/[^a-z]+/gi, "-").toLowerCase()}`} className="relative bg-white border border-border rounded-sm p-3 text-left hover:border-brand transition-colors group">
                            <Info className="w-3 h-3 absolute top-1.5 right-1.5 text-muted-foreground/40 group-hover:text-brand transition-colors" />
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display leading-tight h-6 overflow-hidden pr-3">{s}</div>
                            <div className={`text-2xl font-display font-bold ${STAGE_TONE[s] || ""}`}>{summary.counts[s]}</div>
                        </button>
                    ))}
                    <div className="bg-amber-50 border border-amber-300 rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-widest text-amber-700 font-display">Queries for you</div>
                        <div className="text-2xl font-display font-bold text-amber-600" data-testid="portal-overview-queries">{summary.queries}</div>
                    </div>
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
                <Link to="/portal/info" data-testid="portal-upload-info-btn" className="bg-brand text-white rounded-sm p-5 flex items-center gap-4 hover:bg-brand-hover transition-colors">
                    <UploadCloud className="w-7 h-7 shrink-0" />
                    <div><div className="font-display font-bold">Send us your information</div><div className="text-xs text-white/80">Upload your year-end service charge information any time — no need to wait for our email.</div></div>
                </Link>
                <Link to="/quote" data-testid="portal-quote-btn" className="bg-white border border-border rounded-sm p-5 flex items-center gap-4 hover:border-brand transition-colors">
                    <FileSignature className="w-7 h-7 shrink-0 text-brand" />
                    <div><div className="font-display font-bold">Get a quote</div><div className="text-xs text-muted-foreground">Need a quote for a new scheme? Complete our quick form.</div></div>
                </Link>
            </div>

            <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="font-display font-bold text-xl tracking-tight">Jobs in progress</h2>
                <div className="relative w-full max-w-xs">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input data-testid="portal-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search scheme, property, year end, status, contact…"
                        className="w-full border border-input rounded-sm pl-8 pr-3 py-1.5 text-sm bg-white" />
                </div>
            </div>

            {items === null ? <div className="text-muted-foreground text-sm">Loading…</div> : items.length === 0 ? (
                <div className="bg-white border border-border rounded-sm p-8 text-sm text-muted-foreground">No engagements are currently linked to your managing agent account.</div>
            ) : (
                <div className="bg-white border border-border rounded-sm overflow-x-auto" data-testid="portal-engagement-list">
                    <table className="w-full text-sm min-w-[1120px]">
                        <thead className="border-b border-border bg-slate-50/60 text-left">
                            <tr>
                                <Th label="Scheme / landlord" k="client_name" />
                                <Th label="Property" k="property_name" />
                                <Th label="Reference" k="reference" />
                                <Th label="Address" k="address" />
                                <Th label="Year end" k="current_period_end" />
                                <Th label="Status" k="status" />
                                <Th label="Progress" k="progress" />
                                <Th label="Queries" k="queries" className="text-center" />
                                <Th label="Your contact" k="ma_contact" />
                                <Th label="Qube preparer" k="preparer_name" />
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((e) => (
                                <tr key={e.id} data-testid={`portal-engagement-${e.id}`} onClick={() => nav(`/portal/${e.id}`)}
                                    className="border-b border-border/60 last:border-0 hover:bg-slate-50 cursor-pointer">
                                    <td className="px-3 py-2.5">
                                        <div className="font-display font-bold truncate max-w-[180px]">{e.client_name}</div>
                                        <div className="text-[11px] text-muted-foreground">{typeLabel(e.engagement_type)}</div>
                                    </td>
                                    <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[140px]">{e.property_name || "—"}</td>
                                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                                        <input data-testid={`portal-reference-${e.id}`} defaultValue={e.reference || ""} placeholder="Add ref"
                                            onBlur={(ev) => saveField(e, "reference", ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && ev.currentTarget.blur()}
                                            className="w-24 border border-transparent hover:border-input focus:border-brand rounded-sm px-1.5 py-1 text-xs bg-transparent focus:bg-white" />
                                    </td>
                                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                                        <input data-testid={`portal-address-${e.id}`} defaultValue={e.address || ""} placeholder="Add address"
                                            onBlur={(ev) => saveField(e, "address", ev.target.value)} onKeyDown={(ev) => ev.key === "Enter" && ev.currentTarget.blur()}
                                            className="w-40 border border-transparent hover:border-input focus:border-brand rounded-sm px-1.5 py-1 text-xs bg-transparent focus:bg-white" />
                                    </td>
                                    <td className="px-3 py-2.5 font-num whitespace-nowrap">{e.current_period_end?.slice(0, 10)}</td>
                                    <td className="px-3 py-2.5"><span className={`font-semibold ${STAGE_TONE[statusLabel(e)] || ""}`}>{statusLabel(e)}</span></td>
                                    <td className="px-3 py-2.5 w-36">
                                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-brand" style={{ width: `${progressPct(e) * 100}%` }} /></div>
                                        <div className="text-[10px] font-num text-muted-foreground mt-0.5">{e.progress.done}/{e.progress.total} papers</div>
                                    </td>
                                    <td className="px-3 py-2.5 text-center">
                                        <span className={`text-lg font-display font-bold ${e.awaiting_you > 0 ? "text-amber-600" : "text-emerald-700"}`} data-testid={`portal-awaiting-${e.id}`}>{e.awaiting_you}</span>
                                    </td>
                                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                                        <select data-testid={`portal-ma-contact-${e.id}`} value={e.ma_contact || ""} onChange={(ev) => saveField(e, "ma_contact", ev.target.value)}
                                            className="w-full border border-transparent hover:border-input focus:border-brand rounded-sm px-1.5 py-1 text-xs bg-transparent focus:bg-white">
                                            <option value="">{contacts.length ? "— choose —" : "— none set up —"}</option>
                                            {e.ma_contact && !contacts.some((c) => c.name === e.ma_contact) && <option value={e.ma_contact}>{e.ma_contact}</option>}
                                            {contacts.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2.5 text-muted-foreground truncate max-w-[130px]">{e.preparer_name || "—"}</td>
                                </tr>
                            ))}
                            {filtered.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground italic">No jobs match your search.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </PortalLayout>
    );
}

export function PortalEngagement() {
    const { engagementId } = useParams();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(null);
    const load = () => api.get(`/portal/engagements/${engagementId}/queries`).then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
    useEffect(() => { load(); }, [engagementId]);
    if (!data) return <PortalLayout><div className="text-sm text-muted-foreground">Loading…</div></PortalLayout>;
    const openQ = data.queries.filter((q) => q.status === "open"), closedQ = data.queries.filter((q) => q.status === "closed");
    return (
        <PortalLayout>
            <Link to="/portal" className="text-xs text-brand hover:underline flex items-center gap-1 mb-3"><ChevronLeft className="w-3.5 h-3.5" /> All jobs</Link>
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-display mb-1">Year end {data.engagement.current_period_end?.slice(0, 10)} · {STATUS[data.engagement.status]}</div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">{data.engagement.client_name}</h1>
                </div>
                <div className="text-sm text-muted-foreground"><MessageSquare className="w-4 h-4 inline mr-1" />{openQ.length} open quer{openQ.length === 1 ? "y" : "ies"}</div>
            </div>
            <Section title="Open queries — please respond" empty="No open queries. Thank you!" list={openQ} open={open} setOpen={setOpen} onSent={load} />
            {closedQ.length > 0 && <Section title="Resolved" list={closedQ} open={open} setOpen={setOpen} onSent={load} muted />}
        </PortalLayout>
    );
}

function Section({ title, list, empty, open, setOpen, onSent, muted }) {
    return (
        <div className="mb-8">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-display font-semibold mb-2">{title}</div>
            {list.length === 0 && <div className="bg-white border border-border rounded-sm p-6 text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {empty}</div>}
            <div className="space-y-2">
                {list.map((q) => <PortalQuery key={q.id} q={q} expanded={open === q.id} onToggle={() => setOpen(open === q.id ? null : q.id)} onSent={onSent} muted={muted} />)}
            </div>
        </div>
    );
}

function PortalQuery({ q, expanded, onToggle, onSent, muted }) {
    const [text, setText] = useState("");
    const [atts, setAtts] = useState([]);
    const { user } = useAuth();
    const send = async () => {
        if (!text.trim() && atts.length === 0) return;
        try { await api.post(`/portal/queries/${q.id}/responses`, { text, attachments: atts.map(({ id, ...rest }) => rest) }); setText(""); setAtts([]); toast.success("Response sent"); onSent(); }
        catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    };
    const awaiting = q.status === "open" && (!q.responses.length || q.responses[q.responses.length - 1].user_id !== user?.id);
    return (
        <div className={`bg-white border rounded-sm ${muted ? "border-border opacity-70" : awaiting ? "border-amber-300" : "border-border"}`} data-testid={`portal-query-${q.id}`}>
            <button onClick={onToggle} className="w-full text-left px-5 py-3 flex items-center gap-3">
                {awaiting ? <Clock className="w-4 h-4 text-amber-600 shrink-0" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />}
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{q.title}</div>
                    <div className="text-[11px] text-muted-foreground">{q.wp_title}{q.step_title ? ` · ${q.step_title}` : ""} · raised by {q.raised_by.user_name} on {q.created_at?.slice(0, 10)}</div>
                </div>
                {awaiting && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Awaiting your reply</span>}
                {q.status === "closed" && <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Resolved</span>}
            </button>
            {expanded && (
                <div className="px-5 pb-4 border-t border-border pt-3">
                    {(q.text || q.attachments?.length > 0) && (
                        <div className="text-sm bg-slate-50 border border-border rounded-sm p-3 mb-3">
                            {q.text && <div className="whitespace-pre-line">{q.text}</div>}
                            <AttachmentList items={q.attachments} />
                        </div>
                    )}
                    <div className="space-y-2 mb-3">
                        {q.responses.map((r) => (
                            <div key={r.id} className={`text-sm border-l-2 pl-3 ${r.user_id === user?.id ? "border-amber-400" : "border-brand"}`}>
                                <div className="text-[10px] text-muted-foreground font-num">{r.user_name} · {r.at?.slice(0, 16).replace("T", " ")}</div>
                                {r.text && <div className="whitespace-pre-line">{r.text}</div>}
                                <AttachmentList items={r.attachments} />
                            </div>
                        ))}
                    </div>
                    {q.status === "open" && (
                        <div className="space-y-2">
                            <div className="flex gap-2">
                                <textarea data-testid={`portal-reply-input-${q.id}`} rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type your response… (or tap the mic to dictate)" className="flex-1 border border-input rounded-sm px-3 py-2 text-sm" />
                                <DictateButton testid={`portal-reply-mic-${q.id}`} onAppend={(chunk) => setText((prev) => (prev ? prev + " " : "") + chunk)} />
                                <button data-testid={`portal-reply-send-${q.id}`} onClick={send} className="bg-brand hover:bg-brand-hover text-white px-4 rounded-sm flex items-center gap-1 text-sm"><Send className="w-3.5 h-3.5" /> Send</button>
                            </div>
                            <AttachControls attachments={atts} setAttachments={setAtts} idPrefix={`portal-reply-${q.id}`} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
